import {
  Component,
  viewChild,
  inject,
  input,
  output,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FileUpload } from '@openng/optimus-ui/fileupload';
import { ScheduleImportRow } from 'src/app/model/contacts/schedule-import-row';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ContactListScheduleImport } from 'src/app/model/contacts/contact-list-schedule-import';
import { Contact } from 'src/app/model/contacts/contact';
import { isValid } from 'date-fns';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { NgClass, DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ScheduleBulkDeleteFormComponent } from '../schedule-bulk-delete-form/schedule-bulk-delete-form.component';
import {
  DialogService,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-schedule-import',
  templateUrl: './schedule-import.component.html',
  styleUrls: ['./schedule-import.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    FileUpload,
    ButtonDirective,
    NgClass,
    DatePipe,
    TranslatePipe,
  ],
})
export class ScheduleImportComponent implements OnInit {
  protected _contactListAPI = inject(ContactListApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _dialogService = inject(DialogService);
  protected _translateService = inject(TranslateService);

  readonly contactList = input<ContactList>();
  readonly fileUploadComponent = viewChild<FileUpload>('upload');

  readonly scheduleImported = output();
  readonly scheduleExported = output();

  ref: DynamicDialogRef<ScheduleBulkDeleteFormComponent> | null = null;

  lastUploadedFile!: File | null;
  lastUploadTime!: Date | null;
  importedRows: ScheduleImportRow[] | null = [];
  importedSchedules: ContactListScheduleImport[] = [];

  saveEnabled: boolean = false;
  working: boolean = false;

  contacts!: Contact[];
  adAccountNames: any[] = [];

  ngOnInit(): void {
    this.loadAdAccountNames();
  }

  handleFileUpload(event: { files: File[] }) {
    this.working = true;
    this.lastUploadedFile = event.files[0];
    this.lastUploadTime = new Date();
    this.lastUploadedFile
      .text()
      .then((fileText) => (this.importedRows = this.parseCSV(fileText)))
      .then(() => {
        let didAnyRowFailParse = this.importedRows?.some(
          (row) => row.failedParse
        );
        if (!didAnyRowFailParse) {
          this.saveEnabled = true;
        }
      });
  }

  saveBoundChanges() {
    this.working = true;

    const importSchedules =
      this.importedRows?.map((row) => ({
        adAccountName: row.adAccountName,
        isPrimary: row.primarySecondary.toLowerCase() === 'p' ? true : false,
        startTime: row.startTime
          ? Math.floor(row.startTime.getTime() / 1000)
          : Number.NaN,
        endTime: row.endTime
          ? Math.floor(row.endTime.getTime() / 1000)
          : Number.NaN,
        timezone: row.timezone,
      })) ?? [];
    this._contactListAPI
      .importContactListSchedule(this.contactList()!.id, importSchedules)
      .pipe()
      .subscribe({
        next: (schedules) => {
          this.working = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
          this.scheduleImported.emit();
          // After we complete an import clear out the whole page
          this.fileUploadComponent()!.clear();
          this.lastUploadedFile = null;
          this.lastUploadTime = null;
          this.importedRows = null;
          this.saveEnabled = false;
        },
        error: (error) => {
          this.working = false;
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.INVALID_SCHEDULE',
          });
        },
      });
  }

  cancelFile() {
    this.fileUploadComponent()?.clear();
    this.lastUploadedFile = null;
    this.lastUploadTime = null;
    this.importedRows = null;
    this.saveEnabled = false;
  }

  parseCSV(csvFileText: string): ScheduleImportRow[] {
    const results: ScheduleImportRow[] = [];
    const today = new Date();
    // Split the csv into rows
    const splitFile = csvFileText.trim().split(/\r\n|\n/);
    let headerOK = true;
    splitFile.forEach((row, index) => {
      if (index === 0) {
        // Header row
        const expected_header =
          'User adAccountName,Primary or Secondary,Start Time,End Time,Time Zone';
        if (row !== expected_header) {
          console.error('CSV Header did not match expected format!', row);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'Error',
            bodyKey: 'CSV Header did not match expected format!',
          });
          headerOK = false;
        }
        return;
      }

      if (!headerOK) {
        this.cancelFile();
        return [];
      }

      const splitRow = row.split(',');
      const parsedStartTime = new Date(splitRow[2]);
      const parsedEndTime = new Date(splitRow[3]);

      const newScheduleImportRow: ScheduleImportRow = {
        adAccountName: splitRow[0],
        primarySecondary: splitRow[1],
        startTime: isValid(parsedStartTime) ? parsedStartTime : null,
        endTime: isValid(parsedEndTime) ? parsedEndTime : null,
        timezone: splitRow[4],
        failedParse: false,
        failMessage: '',
      };

      if (!splitRow[0]) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage += 'User adAccountName is empty! ';
        this.saveEnabled = false;
      }
      if (!splitRow[1]) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage += `Primary or Secondary is empty! `;
        this.saveEnabled = false;
      }

      if (!newScheduleImportRow.startTime) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage +=
          'Found startTime value that could not be parsed to a valid Date! ';
        this.saveEnabled = false;
      }

      if (!newScheduleImportRow.endTime) {
        newScheduleImportRow.failedParse = true;
        console.error(
          `Failed parsing end time of row! ${newScheduleImportRow}`
        );
        newScheduleImportRow.failMessage +=
          'Found endTime value that could not be parsed to a valid Date! ';
        this.saveEnabled = false;
      }

      if (
        this.adAccountNames.indexOf(newScheduleImportRow.adAccountName) === -1
      ) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage = `User adAccountName ${newScheduleImportRow.adAccountName} is not a member of this contact list! `;
        this.saveEnabled = false;
      }

      if (
        !(newScheduleImportRow.primarySecondary.toLowerCase() === 'p') &&
        !(newScheduleImportRow.primarySecondary.toLowerCase() === 's')
      ) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage +=
          'Found invalid Primary or Secondary value! ';
        this.saveEnabled = false;
      }

      if (
        newScheduleImportRow.endTime &&
        newScheduleImportRow.endTime <= today
      ) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage += `The end time has already passed. `;
        this.saveEnabled = false;
      }

      if (
        newScheduleImportRow.startTime &&
        newScheduleImportRow.startTime <= today
      ) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage += `The start time has already passed. `;
        this.saveEnabled = false;
      }

      if (
        newScheduleImportRow.startTime &&
        newScheduleImportRow.endTime &&
        newScheduleImportRow.endTime <= newScheduleImportRow.startTime
      ) {
        newScheduleImportRow.failedParse = true;
        newScheduleImportRow.failMessage += `The end time occurs before the start time. `;
        this.saveEnabled = false;
      }

      results.push(newScheduleImportRow);

      return;
    });

    return results;
  }

  loadAdAccountNames() {
    this.contacts =
      this.contactList()?.members.map((member) => member.contact!) ?? [];
    this.contacts.forEach((c) => {
      let adAccountName = c.adAccountName;
      this.adAccountNames.push(adAccountName);
    });

    return this.adAccountNames;
  }

  downloadSchedule() {
    this.scheduleExported.emit();
  }

  async openBulkDelete() {
    const dialogHeader = await firstValueFrom(
      this._translateService.get('CONTACT.LABEL.BULK_DELETE_SCHEDULES')
    );

    this.ref = this._dialogService.open(ScheduleBulkDeleteFormComponent, {
      header: dialogHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: { contactList: this.contactList() },
      focusOnShow: false,
    });
    this.ref?.onClose.subscribe((data: boolean) => {
      if (data) {
        this.scheduleImported.emit();
      }
    });
  }
}
